import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../../locales/translations';
import { Col, Row } from 'reactstrap';
import { DateInput, Select, TextInput } from '@octano/global-ui';
import { Control, Path } from 'react-hook-form';
import { useValidations } from '../../../../hooks/useValidations';
import { months, years } from '../utils';
import { OfferListFormValues } from '../types';

interface Texts {
  specificDate: string;
  specificMonthDay: string;
  nextMonth: string;
  nextMonthDay: string;
  form: {
    selectDay: string;
    selectMonth: string;
    selectDate: string;
    selectYear: string;
    validityRange: string;
    selectDayOptional: string;
  };
}

type AllowedFormValues = OfferListFormValues;

interface UseFirstInstallmentRuleProps<T extends AllowedFormValues> {
  key: string;
  control: Control<T>;
  fieldPrefix?: string;
}

export default function useFirstInstallmentRule<T extends AllowedFormValues>({
  key,
  control,
  fieldPrefix = '',
}: UseFirstInstallmentRuleProps<T>) {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_ACADEMIC_OFFER);

  const { msgValidations } = useValidations();

  const getFieldName = useCallback(
    (name: string) => (fieldPrefix ? `${fieldPrefix}_${name}` : name),
    [fieldPrefix],
  );

  const texts = useMemo(() => {
    const prefix = 'processList.modalForm.firstExpirationRuleOptions';
    return {
      firstExpirationRuleSubTitle: t(`${prefix}.firstExpirationRuleSubTitle`),
      specificDate: t(`${prefix}.specificDate`),
      specificMonthDay: t(`${prefix}.firstDayOfSpecificMonth`),
      nextMonth: t(`${prefix}.nextMonth`),
      nextMonthDay: t(`${prefix}.nextMonthDay`),
      specificDateSubtitle: t(`${prefix}.specificDateSubtitle`),
      specificMonthDaySubtitle: t(`${prefix}.firstDayOfSpecificMonthSubtitle`),
      nextMonthSubtitle: t(`${prefix}.nextMonthSubtitle`),
      nextMonthDaySubtitle: t(`${prefix}.nextMonthDaySubtitle`),
      form: {
        selectDay: t(`${prefix}.form.selectDay`),
        selectDayOptional: t(`${prefix}.form.selectDayOptional`),
        selectMonth: t(`${prefix}.form.selectMonth`),
        selectYear: t(`${prefix}.form.selectYear`),
        selectDate: t(`${prefix}.form.selectDate`),
        validityRange: t(`${prefix}.form.validityRange`),
      },
    };
  }, [t]);

  const firstInstallmentRuleOptions = useMemo(() => {
    return [
      {
        label: texts.specificDate,
        value: 'specificDate',
      },
      {
        label: texts.specificMonthDay,
        value: 'specificMonthDay',
      },
      { label: texts.nextMonth, value: 'nextMonth' },
      { label: texts.nextMonthDay, value: 'nextMonthDay' },
    ];
  }, [texts]);

  const dayField = useCallback(
    (texts: Texts, optional: boolean = false) => (
      <Col xs={12} md={6}>
        <TextInput
          name={getFieldName('selectDay') as Path<T>}
          control={control}
          label={optional ? texts.form.selectDayOptional : texts.form.selectDay}
          rules={{
            required: optional ? false : msgValidations.required,
            maxLength: {
              value: 2,
              message: 'Máximo 2 dígitos',
            },
            pattern: {
              value: /^[0-9]*$/,
              message: msgValidations.invalidIntNumeric,
            },
          }}
        />
      </Col>
    ),
    [control, msgValidations, getFieldName],
  );

  const monthField = useCallback(
    (texts: Texts) => (
      <>
        <Col xs={12} md={4}>
          <Select
            name={getFieldName('selectMonth') as Path<T>}
            control={control}
            label={texts.form.selectMonth}
            options={months}
            rules={{ required: msgValidations.required }}
          />
        </Col>
        <Col xs={12} md={4}>
          <Select
            name={getFieldName('selectYear') as Path<T>}
            control={control}
            label={texts.form.selectYear}
            options={years}
            rules={{ required: msgValidations.required }}
          />
        </Col>
      </>
    ),
    [control, msgValidations, getFieldName],
  );

  const dateField = useCallback(
    (texts: Texts) => (
      <Col xs={12} md={6}>
        <DateInput
          name={getFieldName('selectDate') as Path<T>}
          control={control}
          label={texts.form.selectDate}
          rules={{ required: msgValidations.required }}
        />
      </Col>
    ),
    [control, msgValidations, getFieldName],
  );

  const validityRangeField = useCallback(
    (texts: Texts, md: number = 6) => (
      <Col xs={12} md={md}>
        <TextInput
          name={getFieldName('validityRange') as Path<T>}
          control={control}
          label={texts.form.validityRange}
          rules={{
            pattern: {
              value: /^[0-9]*$/,
              message: msgValidations.invalidIntNumeric,
            },
          }}
        />
      </Col>
    ),
    [control, msgValidations, getFieldName],
  );

  const formFields = useMemo(() => {
    switch (key) {
      case 'specificDate':
        return (
          <>
            <p className="fs-14 mb-3">{texts.specificDateSubtitle}</p>
            <Row>
              {dateField(texts)}
              {validityRangeField(texts)}
            </Row>
          </>
        );
      case 'specificMonthDay':
        return (
          <>
            <p className="fs-14 mb-3">{texts.specificMonthDaySubtitle}</p>
            <Row>
              {monthField(texts)}
              {validityRangeField(texts, 4)}
            </Row>
          </>
        );
      case 'nextMonth':
        return (
          <>
            <p className="fs-14 mb-3">{texts.nextMonthSubtitle}</p>
            <Row>
              {dayField(texts, true)}
              {validityRangeField(texts)}
            </Row>
          </>
        );
      case 'nextMonthDay':
        return (
          <>
            <p className="fs-14 mb-3">{texts.nextMonthDaySubtitle}</p>
            <Row>
              {dayField(texts)}
              {validityRangeField(texts)}
            </Row>
          </>
        );
      default:
        return null;
    }
  }, [key, texts, dayField, monthField, dateField, validityRangeField]);

  return {
    options: firstInstallmentRuleOptions,
    formFields,
  };
}
